import { DateTime } from "luxon";
import type { Department } from "../../contexts/AuthProvider";
import { getStaffingDayStart } from "../../utils/dateUtils";
import type { NurseFullRole, NurseInShift } from "../../utils/models";
import { sortByLocationOrder } from "../../utils/multiColumnSorting";
import { buildNurseUnionMap, getDepartmentNurseTypes } from "../../utils/utilFunctions";
import { assembleSourceRows, type SourceRowsResult } from "../dailyPrintout/phases/sourceRows";
import { type AiPrintoutCounterparts, pairCounterparts } from "./aiPrintoutCounterparts";
import type { AiPrintoutStatsInputs } from "./aiPrintoutMetrics";
import { type RequiredSlot, requiredSlotsOf, slotRoleOf } from "./aiPrintoutRequirements";
import { type AiPrintoutOrders, type AiPrintoutTimeContext, textOrNull } from "./aiPrintoutRowMappers";
import { type AiPrintoutTimeGroups, resolveTimeGroups, type TimeGroupKeysOf, timeGroupKeysOf } from "./aiPrintoutTimeGroups";
import type { BuildAiPrintoutDataInput } from "./buildAiPrintoutData";

/** The day's two sides: the scheduled shifts, and the punches the actual totals are computed over. */
export type DaySides = { scheduled: NurseInShift[]; punches: NurseInShift[] };

/** What every part of the build shares, resolved once. */
export type BuildContext = {
	input: BuildAiPrintoutDataInput;
	scheduleDay: DateTime;
	context: AiPrintoutTimeContext;
	/** The shifts on the side the page shows: scheduled, or the actual day's rows. */
	shown: NurseInShift[];
	source: SourceRowsResult;
	sides: DaySides;
	requiredSlots: RequiredSlot[];
	orders: AiPrintoutOrders;
	timeGroups: AiPrintoutTimeGroups | null;
	keysOf: TimeGroupKeysOf;
	counterparts: AiPrintoutCounterparts;
	statsInputs: AiPrintoutStatsInputs;
	unionByNurseId: Map<number, string>;
};

/** The viewed day as a date in the department's timezone — the calendar date, whatever zone `date` carries. */
const scheduleDayOf = (date: DateTime, department: Department): DateTime => {
	const isoDate = date.toISODate();
	if (isoDate === null) throw new Error(`Invalid printout date: ${date.invalidExplanation}`);
	return DateTime.fromISO(isoDate, { zone: department.timezone });
};

/** Value → its index in an already-ordered list. */
const ranksOf = (ordered: readonly string[]): Map<string, number> => new Map(ordered.map((value, index) => [value, index]));

/**
 * The department's orders over everything on the page, with the helpers the app's views use: the location
 * order of `sortByLocationOrder` (as the Detailed printout and Day view sort locations), and the role list of
 * `getDepartmentNurseTypes` (as the Day view lists roles) — fed the day's own people, gaps, positions and census
 * locations too, so every role and location in `data` has a place.
 */
const buildOrders = (shown: NurseInShift[], source: SourceRowsResult, requiredSlots: RequiredSlot[], input: BuildAiPrintoutDataInput): AiPrintoutOrders => {
	const slots = [...source.missingLocations, ...requiredSlots.map((required) => required.slot)];
	const locationNames = [...shown.map((shift) => shift.location), ...slots.map((slot) => slot.location), ...source.censusByLocationName.keys()].filter(
		(location): location is string => textOrNull(location) !== null,
	);
	const slotRoles = slots.map(slotRoleOf).filter((role): role is NurseFullRole => role !== null);
	const roleSources = [...input.hospitalData.nurses, ...input.scheduledShifts, ...input.actualDay.actualShifts, ...slotRoles.map((role) => ({ role }))];
	return {
		locationRanks: ranksOf(sortByLocationOrder([...new Set(locationNames)], input.customLocationOrder)),
		roleRanks: ranksOf(getDepartmentNurseTypes(input.department.roles, roleSources)),
	};
};

const statsInputsOf = (input: BuildAiPrintoutDataInput): AiPrintoutStatsInputs => ({
	department: input.department,
	hospitalData: input.hospitalData,
	weekIndex: input.weekIndex,
	dayIndex: input.date.weekdayISO,
	jobManagementEnabled: input.jobManagementEnabled,
	isSnf: input.isSnf,
	rolesToDisplay: input.rolesToDisplay,
	hppdIncludeTrainees: input.hppdIncludeTrainees,
	censusGrouping: input.censusGrouping,
	findDepartmentLocation: input.findDepartmentLocation,
});

/** The Detailed pipeline's own source rows for the shown side, with every location selected and gaps on. */
const sourceRowsOf = (input: BuildAiPrintoutDataInput, shown: NurseInShift[]): SourceRowsResult =>
	assembleSourceRows({
		hospitalData: input.hospitalData,
		weekIndex: input.weekIndex,
		dayIndex: input.date.weekdayISO,
		department: input.department,
		date: input.date,
		staffingNurses: shown,
		// Source rows never read time off; it is mapped on its own.
		ptoShifts: [],
		selectedLocations: [],
		availableLocations: [],
		// On, for the location census the Detailed printout's "Show location census" prints.
		showLocationCensus: true,
		timezone: input.department.timezone,
		dayStart: input.department.dayStart,
		printGaps: true,
	});

export const createBuildContext = (input: BuildAiPrintoutDataInput): BuildContext => {
	const { date, department, actualDay } = input;
	const scheduleDay = scheduleDayOf(date, department);
	const shown = input.isActual ? actualDay.actualShifts : input.scheduledShifts;
	const source = sourceRowsOf(input, shown);
	const requiredSlots = requiredSlotsOf(source.departmentLocationsRequired, date, department);
	const timeGroups = resolveTimeGroups({
		config: input.customTimeGroups,
		timezone: department.timezone,
		dayStart: department.dayStart,
		otherLabel: input.labels.otherTimeGroup,
	});
	const standIns = new Set(actualDay.substitutedShifts);
	return {
		input,
		scheduleDay,
		context: { timezone: department.timezone, dayStartAt: getStaffingDayStart(scheduleDay, department.dayStart), locale: input.locale },
		shown,
		source,
		// The actual totals are over punches, so a scheduled shift standing in for one is not on that side.
		sides: { scheduled: input.scheduledShifts, punches: actualDay.actualShifts.filter((shift) => !standIns.has(shift)) },
		requiredSlots,
		orders: buildOrders(shown, source, requiredSlots, input),
		timeGroups,
		keysOf: timeGroupKeysOf(timeGroups),
		counterparts: pairCounterparts(input.scheduledShifts, actualDay, input.isActual),
		statsInputs: statsInputsOf(input),
		unionByNurseId: buildNurseUnionMap(input.hospitalData.nurses),
	};
};
